import { isDevelopment } from '@core/blocks/edu-match/utils/determineEnv';
import { type Flags } from '@core/context/FeatureFlagsContext';
import { MonarchEditableAsset, NoPiiTestAsset } from '@core/context/types';
import { qualifyEduRequest } from '@core/services/leadDelivery';

import { type InjectionKey } from './getQuestionInjectionSchema';
import { type GCPParams, generateQualifyPayload, getQualifiedPartnerKeys } from './utils/qualifications';

type GNCPSParams = {
  flags: Flags;
  noPii: NoPiiTestAsset | false | 'conditions_not_met' | null | undefined;
  startTime?: number | null;
} & GCPParams;

export const isEditableAsset = (asset?: unknown) =>
  asset &&
  (asset as MonarchEditableAsset)?.questionStepId &&
  (asset as MonarchEditableAsset)?.questionStepName &&
  asset !== 'conditions_not_met';

const getInjectionKeys = async ({
  dcs,
  ipInferredPostalCode,
  flags,
  publisher,
  noPii,
  startTime,
}: GNCPSParams): Promise<InjectionKey[]> => {
  // Check for noPii variants
  // noPiiZipOnly = zip-only flow (name and contact info questions removed)
  // noPiiZipName = name + zip flow (contact info questions removed)
  // control = standard experience
  const isNoPiiObject = typeof noPii === 'object' && noPii !== null;
  const variant = isNoPiiObject && 'variant' in (noPii as Record<string, unknown>) ? (noPii as any).variant : null;
  const startDateValues =
    isNoPiiObject && 'startDateValues' in (noPii as Record<string, unknown>) ? (noPii as any).startDateValues : null;

  // Check if startTime is in the startDateValues array to enable the test
  const normalizeStartTime = (value: number | string | null | undefined) => {
    if (value === undefined) return undefined;
    if (value === null) return null;
    return Number(value);
  };

  const normalizedStartTime = normalizeStartTime(startTime as any);
  const hasSelectedStartTime = normalizedStartTime !== undefined;
  const isTestEnabled =
    Array.isArray(startDateValues) && hasSelectedStartTime
      ? startDateValues.includes(normalizedStartTime as number | null)
      : false;

  // Collect all editable-asset keys so partner overrides can be merged in later.
  const editableAssetKeys = Object.entries(flags ?? {}).reduce<InjectionKey[]>((acc, [key, value]) => {
    if (isEditableAsset(value)) {
      acc.push(key as InjectionKey);
    }
    return acc;
  }, []);

  const unique = (keys: InjectionKey[]) => Array.from(new Set(keys));

  // Handle noPii variants
  const noPiiVariantKey =
    isTestEnabled && (variant === 'noPiiZipOnly' || variant === 'noPiiZipName') ? (variant as InjectionKey) : null;

  const qualifyPayload = generateQualifyPayload({ dcs, ipInferredPostalCode, publisher });

  const res = await qualifyEduRequest(qualifyPayload);

  if (res instanceof Error) {
    return [];
  }

  const partnerKeys = getQualifiedPartnerKeys(res.qualified, flags);

  let injectionKeys = unique([...partnerKeys, ...editableAssetKeys]);

  // Drop generic noPii keys so we only apply the specific active variant once.
  injectionKeys = injectionKeys.filter(
    (key) => key !== 'noPii' && key !== 'noPiiZipOnly' && key !== 'noPiiZipName' && key !== 'noPiiTest'
  );

  if (noPiiVariantKey) {
    const mergedKeys = unique([...injectionKeys, noPiiVariantKey]);

    if (mergedKeys.includes('EMCC')) {
      return mergedKeys.filter((key) => !key.includes('EAB'));
    }

    if (isDevelopment()) {
      console.log('Flags:', flags);
      console.log('Injection Keys:', mergedKeys);
    }

    return mergedKeys;
  }

  // Hardcoded rules
  if (injectionKeys.includes('EMCC')) {
    // Remove EAB keys if EMCC is present
    injectionKeys = injectionKeys.filter((key) => !key.includes('EAB'));
  }

  if (isDevelopment()) {
    console.log('Flags:', flags);
    console.log('Injection Keys:', injectionKeys);
  }

  return injectionKeys;
};

export default getInjectionKeys;
